import { useState, useCallback } from 'react';
import { Search, Plus, Pencil, X, ChevronRight, TrendingUp, Trash2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useClients, useCreateClient, useUpdateClient, useDeleteClient, type ClientDTO } from '../../hooks/useClients';
import { useClientRecipeRatesList } from '../../hooks/useClientRecipeRates';
import { useAuthStore } from '../../stores/auth-store';
import { createClientSchema, type CreateClientInput } from '@shared/validation/client-schemas';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { toast } from '../../components/ui/Toast';

export function ClientListPage() {
  const user = useAuthStore((s) => s.user);
  const canEdit = ['administrativo', 'gerente', 'operaciones'].includes(user?.role ?? '');

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout>>();
  const [page, setPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editClient, setEditClient] = useState<ClientDTO | null>(null);
  const [selectedClient, setSelectedClient] = useState<ClientDTO | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'tarifas'>('info');

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      if (debounceTimer) clearTimeout(debounceTimer);
      const timer = setTimeout(() => {
        setDebouncedSearch(value);
        setPage(1);
      }, 300);
      setDebounceTimer(timer);
    },
    [debounceTimer],
  );

  const handleSelectClient = (client: ClientDTO) => {
    if (selectedClient?.id === client.id) {
      setSelectedClient(null);
    } else {
      setSelectedClient(client);
      setActiveTab('info');
    }
  };

  const { data, isLoading, isError, refetch } = useClients({
    search: debouncedSearch || undefined,
    page,
    limit: 20,
  });

  if (isLoading) {
    return (
      <div className="space-y-6 fade-up">
        <h1 className="text-2xl font-bold text-brand-dark">Clientes</h1>
        <Card><div className="p-6"><SkeletonTable rows={5} cols={4} /></div></Card>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6 fade-up">
        <h1 className="text-2xl font-bold text-brand-dark">Clientes</h1>
        <Card>
          <div className="px-6 py-12 text-center">
            <p className="text-sm text-gray-500">Error al cargar clientes</p>
            <Button variant="secondary" className="mt-4" onClick={() => refetch()}>Reintentar</Button>
          </div>
        </Card>
      </div>
    );
  }

  const clients = data?.data ?? [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6 fade-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-brand-dark">Clientes</h1>
        {canEdit && (
          <Button variant="primary" leftIcon={<Plus size={16} />} onClick={() => setShowCreateModal(true)}>
            Nuevo Cliente
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          className="w-full rounded-lg border border-gray-200 bg-white/80 py-2 pl-9 pr-3 text-sm placeholder:text-gray-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
          placeholder="Buscar por nombre, RUC o email..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </div>

      {/* Master-detail layout */}
      <div className={`flex gap-4 ${selectedClient ? 'lg:flex-row flex-col' : ''}`}>
        {/* List panel */}
        <div className={selectedClient ? 'lg:w-2/5 w-full' : 'w-full'}>
          {clients.length === 0 ? (
            <Card>
              <div className="px-6 py-16 text-center">
                <h2 className="text-lg font-semibold text-gray-600">
                  {debouncedSearch ? 'Sin resultados' : 'No hay clientes registrados'}
                </h2>
                <p className="mt-1 text-sm text-gray-400">
                  {debouncedSearch
                    ? `No encontramos clientes que coincidan con "${debouncedSearch}"`
                    : 'Registra el primer cliente para comenzar.'}
                </p>
              </div>
            </Card>
          ) : (
            <Card>
              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <th className="px-4 py-3">Nombre</th>
                      {!selectedClient && <th className="px-4 py-3">RUC / CI</th>}
                      {!selectedClient && <th className="px-4 py-3">Teléfono</th>}
                      {!selectedClient && <th className="px-4 py-3">Email</th>}
                      <th className="px-4 py-3 w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((client) => (
                      <tr
                        key={client.id}
                        onClick={() => handleSelectClient(client)}
                        className={`border-b border-gray-50 cursor-pointer transition-colors ${
                          selectedClient?.id === client.id
                            ? 'bg-brand-blue/5 border-l-2 border-l-brand-blue'
                            : 'hover:bg-gray-50/30'
                        }`}
                      >
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-800">{client.nombre}</p>
                          {selectedClient && (
                            <p className="text-xs text-gray-400">{client.ruc_ci ?? '—'}</p>
                          )}
                        </td>
                        {!selectedClient && <td className="px-4 py-3 text-gray-600">{client.ruc_ci ?? '—'}</td>}
                        {!selectedClient && <td className="px-4 py-3 text-gray-600">{client.telefono ?? '—'}</td>}
                        {!selectedClient && <td className="px-4 py-3 text-gray-600">{client.email ?? '—'}</td>}
                        <td className="px-4 py-3 text-right">
                          <ChevronRight
                            size={14}
                            className={`inline-block transition-transform ${
                              selectedClient?.id === client.id ? 'rotate-90 text-brand-blue' : 'text-gray-300'
                            }`}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="sm:hidden divide-y divide-gray-50">
                {clients.map((client) => (
                  <div
                    key={client.id}
                    onClick={() => handleSelectClient(client)}
                    className={`flex items-center justify-between px-4 py-3 cursor-pointer ${
                      selectedClient?.id === client.id ? 'bg-brand-blue/5' : 'hover:bg-gray-50/30'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-800 truncate">{client.nombre}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {client.ruc_ci ?? ''} {client.telefono ? `· ${client.telefono}` : ''}
                      </p>
                    </div>
                    <ChevronRight
                      size={14}
                      className={`ml-2 transition-transform ${
                        selectedClient?.id === client.id ? 'rotate-90 text-brand-blue' : 'text-gray-300'
                      }`}
                    />
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 border-t border-gray-100 p-3">
                  <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                    Anterior
                  </Button>
                  <span className="text-xs text-gray-500">
                    {page} / {pagination.totalPages}
                  </span>
                  <Button variant="ghost" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage((p) => p + 1)}>
                    Siguiente
                  </Button>
                </div>
              )}
            </Card>
          )}
        </div>

        {/* Detail panel */}
        {selectedClient && (
          <div className="lg:w-3/5 w-full">
            <ClientDetailPanel
              client={selectedClient}
              canEdit={canEdit}
              onClose={() => setSelectedClient(null)}
              onEdit={(c) => setEditClient(c)}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <ClientFormModal
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {/* Edit Modal */}
      {editClient && (
        <ClientFormModal
          client={editClient}
          onClose={() => setEditClient(null)}
        />
      )}
    </div>
  );
}

// ── Client Detail Panel ────────────────────────────────────────────
function ClientDetailPanel({
  client,
  canEdit,
  onClose,
  onEdit,
  activeTab,
  onTabChange,
}: {
  client: ClientDTO;
  canEdit: boolean;
  onClose: () => void;
  onEdit: (c: ClientDTO) => void;
  activeTab: 'info' | 'tarifas';
  onTabChange: (tab: 'info' | 'tarifas') => void;
}) {
  const { data: rates, isLoading } = useClientRecipeRatesList(client.id);

  const tabs: { id: 'info' | 'tarifas'; label: string }[] = [
    { id: 'info', label: 'Información' },
    { id: 'tarifas', label: `Tarifas${rates && rates.length > 0 ? ` (${rates.length})` : ''}` },
  ];

  return (
    <Card className="sticky top-4">
      {/* Panel header */}
      <div className="flex items-start justify-between border-b border-gray-100 px-5 py-4">
        <div>
          <h2 className="text-base font-semibold text-gray-800">{client.nombre}</h2>
          {client.ruc_ci && <p className="text-xs text-gray-400 mt-0.5">RUC/CI: {client.ruc_ci}</p>}
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <>
              <button
                onClick={() => onEdit(client)}
                className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-brand-blue"
                title="Editar cliente"
              >
                <Pencil size={14} />
              </button>
              <DeleteClientButton clientId={client.id} onDeleted={onClose} />
            </>
          )}
          <button
            onClick={onClose}
            className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            title="Cerrar"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-100 px-5">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`py-3 pr-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-brand-blue text-brand-blue'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="p-5">
        {activeTab === 'info' && <ClientInfoTab client={client} />}
        {activeTab === 'tarifas' && <ClientTarifasTab rates={rates} isLoading={isLoading} />}
      </div>
    </Card>
  );
}

// ── Info Tab ───────────────────────────────────────────────────────
function ClientInfoTab({ client }: { client: ClientDTO }) {
  const fields = [
    { label: 'Nombre', value: client.nombre },
    { label: 'RUC / CI', value: client.ruc_ci },
    { label: 'Teléfono', value: client.telefono },
    { label: 'Email', value: client.email },
    { label: 'Dirección', value: client.direccion },
  ];

  return (
    <dl className="space-y-3">
      {fields.map(({ label, value }) => (
        <div key={label} className="flex gap-3">
          <dt className="w-24 shrink-0 text-xs font-medium text-gray-400 pt-0.5">{label}</dt>
          <dd className="text-sm text-gray-700">{value ?? <span className="text-gray-300">—</span>}</dd>
        </div>
      ))}
    </dl>
  );
}

// ── Tarifas Tab ────────────────────────────────────────────────────
function ClientTarifasTab({
  rates,
  isLoading,
}: {
  rates: import('../../hooks/useClientRecipeRates').ClientRecipeRateDTO[] | undefined;
  isLoading: boolean;
}) {
  if (isLoading) {
    return <SkeletonTable rows={3} cols={3} />;
  }

  if (!rates || rates.length === 0) {
    return (
      <div className="py-10 text-center">
        <TrendingUp size={32} className="mx-auto text-gray-200 mb-3" />
        <p className="text-sm font-medium text-gray-500">Sin tarifas negociadas</p>
        <p className="text-xs text-gray-400 mt-1">
          Las tarifas se registran automáticamente al aprobar una proforma para este cliente.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-400 mb-3">
        Utilidades acordadas por APU. Se actualizan al aprobar cada proforma.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-medium uppercase tracking-wider text-gray-400 border-b border-gray-100">
              <th className="pb-2 pr-4">APU</th>
              <th className="pb-2 pr-4">Código</th>
              <th className="pb-2 pr-4 text-right">Utilidad</th>
              <th className="pb-2 text-right">Actualizado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {rates.map((rate) => (
              <tr key={rate.recipe_id} className="hover:bg-gray-50/50">
                <td className="py-2 pr-4">
                  <p className="font-medium text-gray-800 text-xs leading-snug">{rate.recipe_nombre}</p>
                </td>
                <td className="py-2 pr-4">
                  <span className="text-xs font-mono text-gray-500">{rate.recipe_codigo}</span>
                </td>
                <td className="py-2 pr-4 text-right">
                  <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700">
                    {rate.porcentaje_utilidad}%
                  </span>
                </td>
                <td className="py-2 text-right text-xs text-gray-400">
                  {new Date(rate.updated_at).toLocaleDateString('es-EC', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Client Form Modal (Create / Edit) ──────────────────────────────
function ClientFormModal({
  client,
  onClose,
}: {
  client?: ClientDTO;
  onClose: () => void;
}) {
  const isEdit = !!client;
  const createMutation = useCreateClient();
  const updateMutation = useUpdateClient();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateClientInput>({
    resolver: zodResolver(createClientSchema),
    defaultValues: client
      ? {
          nombre: client.nombre,
          rucCi: client.ruc_ci,
          telefono: client.telefono,
          email: client.email,
          direccion: client.direccion,
        }
      : {},
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (isEdit && client) {
        await updateMutation.mutateAsync({ id: client.id, data });
        toast('success', 'Cliente actualizado');
      } else {
        await createMutation.mutateAsync(data);
        toast('success', 'Cliente creado exitosamente');
      }
      onClose();
    } catch {
      // Error handled by api-client toast
    }
  });

  return (
    <Modal isOpen title={isEdit ? 'Editar Cliente' : 'Nuevo Cliente'} onClose={onClose} size="md">
      <form onSubmit={onSubmit} className="space-y-4">
        <Input
          label="Nombre *"
          placeholder="Nombre del cliente o empresa"
          error={errors.nombre?.message}
          {...register('nombre')}
        />
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="RUC / Cedula"
            placeholder="0992345678001"
            error={errors.rucCi?.message}
            {...register('rucCi')}
          />
          <Input
            label="Telefono"
            placeholder="099 123 4567"
            error={errors.telefono?.message}
            {...register('telefono')}
          />
        </div>
        <Input
          label="Email"
          type="email"
          placeholder="cliente@ejemplo.com"
          error={errors.email?.message}
          {...register('email')}
        />
        <Input
          label="Direccion"
          placeholder="Av. Principal y Calle 2"
          error={errors.direccion?.message}
          {...register('direccion')}
        />

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose} type="button">Cancelar</Button>
          <Button
            variant="primary"
            type="submit"
            isLoading={createMutation.isPending || updateMutation.isPending}
          >
            {isEdit ? 'Guardar' : 'Crear Cliente'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function DeleteClientButton({ clientId, onDeleted }: { clientId: string; onDeleted: () => void }) {
  const deleteMutation = useDeleteClient();
  return (
    <button
      className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500"
      title="Eliminar cliente"
      onClick={async () => {
        if (window.confirm('¿Eliminar este cliente? Solo es posible si no tiene proyectos asociados.')) {
          await deleteMutation.mutateAsync(clientId);
          onDeleted();
        }
      }}
    >
      <Trash2 size={14} />
    </button>
  );
}
